export type Difficulty = 'beginner' | 'intermediate' | 'advanced';

export interface Lesson {
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  order: number;
  difficulty: Difficulty;
  featured_image: string | null;
  prev_slug: string | null;
  next_slug: string | null;
  seo_title: string | null;
  seo_description: string | null;
  seo_keywords: string | null;
  reading_time: number;
}

const modules = import.meta.glob('/content/learn/*.md', {
  eager: true,
  query: '?raw',
  import: 'default',
}) as Record<string, string>;

/** Parse YAML frontmatter without gray-matter (which needs Node Buffer) */
function parseFrontmatter(raw: string): { data: Record<string, string>; content: string } {
  const match = raw.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n([\s\S]*)$/);
  if (!match) return { data: {}, content: raw };

  const yamlBlock = match[1];
  const content = match[2];
  const data: Record<string, string> = {};

  for (const line of yamlBlock.split('\n')) {
    const idx = line.indexOf(':');
    if (idx === -1) continue;
    const key = line.slice(0, idx).trim();
    let value = line.slice(idx + 1).trim();
    if ((value.startsWith('"') && value.endsWith('"')) || (value.startsWith("'") && value.endsWith("'"))) {
      value = value.slice(1, -1);
    }
    data[key] = value;
  }

  return { data, content };
}

function parseLesson(raw: string, filepath: string): Lesson {
  const { data, content } = parseFrontmatter(raw);

  const slug =
    data.slug ||
    filepath
      .split('/')
      .pop()!
      .replace(/\.md$/, '')
      .replace(/^\d+-/, '');

  const wordCount = content.trim().split(/\s+/).length;
  const reading_time = Math.max(1, Math.ceil(wordCount / 200));

  return {
    slug,
    title: data.title || slug,
    excerpt: data.excerpt || '',
    content,
    order: parseInt(data.order, 10) || 0,
    difficulty: (data.difficulty as Difficulty) || 'beginner',
    featured_image: data.featured_image || null,
    prev_slug: data.prev_slug || null,
    next_slug: data.next_slug || null,
    seo_title: data.seo_title || null,
    seo_description: data.seo_description || null,
    seo_keywords: data.seo_keywords || null,
    reading_time,
  };
}

let _lessons: Lesson[] | null = null;

function loadLessons(): Lesson[] {
  if (_lessons) return _lessons;
  _lessons = Object.entries(modules)
    .map(([filepath, raw]) => parseLesson(raw, filepath))
    .sort((a, b) => a.order - b.order);
  return _lessons;
}

export function getAllLessons(): Lesson[] {
  return loadLessons();
}

export function getLessonBySlug(slug: string): Lesson | undefined {
  return loadLessons().find((l) => l.slug === slug);
}

export function getAllSlugs(): string[] {
  return loadLessons().map((l) => l.slug);
}

export function getAdjacentLessons(slug: string): { prev: Lesson | null; next: Lesson | null } {
  const lessons = loadLessons();
  const idx = lessons.findIndex((l) => l.slug === slug);
  if (idx === -1) return { prev: null, next: null };
  return {
    prev: idx > 0 ? lessons[idx - 1] : null,
    next: idx < lessons.length - 1 ? lessons[idx + 1] : null,
  };
}
